import { Bundle, QuestionnaireResponse, QuestionnaireResponseItem, Resource } from 'fhir/r4';
import { ArrivalBoardColumn, ArrivalBoardColumnSource } from '../entities/arrival-board-configuration';
import { ArrivalBoardItem } from '../entities/arrival-board-item';

/** Prefix the configured response column paths carry, e.g. 'QuestionnaireResponse.id'. */
const RESPONSE_PATH_PREFIX = 'QuestionnaireResponse.';

/** What an ArrivalBoardItem cell may hold — matches the row's index signature. */
type CellValue = string | number | boolean | undefined;

/** First answer of a QuestionnaireResponse item, flattened to a single cell value. */
type Answer = NonNullable<QuestionnaireResponseItem['answer']>[number];

/**
 * Turn one polled searchset into board rows.
 *
 * Pure by design so it unit-tests against a canned Bundle without HTTP: the backend derives a
 * single FHIR search and streams the result back unmapped, so all of the row shaping happens here.
 *
 * A live Bundle carries **QuestionnaireResponse resources only** — the AmbER HAPI server refuses
 * `_include=QuestionnaireResponse:subject` and will not read `Patient` at all, so `subject` is a
 * dangling reference and must never be followed. Everything a row shows is on the response: the
 * demographics arrive as `voranmeldung.*` answers, the case key as the resource id.
 *
 * Two behaviours are load-bearing and easy to undo by accident:
 *
 * - **Newest-per-caseId collapse.** Live it never fires — one resource per mission, so ids are
 *   unique per Bundle — but it is the guard that stops a duplicate id from producing two rows with
 *   the same diff key, which would corrupt the differ's tracking map.
 * - **Rows come back oldest-first.** ArrivalBoardDiffer stamps an ascending `arrivalIndex` on first
 *   sight and sorts descending by it, so later in this array renders higher up the board. The
 *   Bundle is newest-first, hence the reverse at the end. Fix it here, never in the differ, whose
 *   ordering the flash and row-enter animations depend on.
 *
 * @param bundle - searchset from `GET arrival-board/:boardId`
 * @param columns - the board's configured columns; each contributes one keyed cell per row
 */
export function mapBundleToItems(bundle: Bundle, columns: ArrivalBoardColumn[]): ArrivalBoardItem[] {
    const rows: ArrivalBoardItem[] = [];
    const seenCaseIds = new Set<string>();

    for (const entry of bundle?.entry ?? []) {
        if (entry.search?.mode !== 'match') continue;
        const response = entry.resource;
        if (!isQuestionnaireResponse(response)) continue;

        // The resource id IS the amber missionId (also mirrored in `identifier`), so no join and no
        // identifier scan: the grouping key sits on the resource that carries the answers. An
        // amendment amends that resource in place, so the key is stable across updates too.
        const caseId = response.id;
        // No case key means no diff key and no leading column, so the row cannot be displayed.
        if (caseId === undefined) continue;
        if (seenCaseIds.has(caseId)) continue;

        seenCaseIds.add(caseId);
        rows.push(buildRow(caseId, response, columns));
    }

    return rows.reverse();
}

// `BundleEntry.resource` is typed as the base `Resource`, whose `resourceType` is a plain `string`,
// so a resourceType comparison does not narrow on its own — this guard does it.

function isQuestionnaireResponse(resource: Resource | undefined): resource is QuestionnaireResponse {
    return resource?.resourceType === 'QuestionnaireResponse';
}

function buildRow(caseId: string, response: QuestionnaireResponse, columns: ArrivalBoardColumn[]): ArrivalBoardItem {
    const row: ArrivalBoardItem = { caseId };
    for (const column of columns) {
        row[column.key] =
            column.source === ArrivalBoardColumnSource.RESPONSE
                ? readResponseCell(response, column.path, caseId)
                : readAnswerCell(response.item, column.linkId);
    }
    // Re-stamped last: a column keyed 'caseId' must not be able to blank the diff key.
    row.caseId = caseId;
    return row;
}

function readResponseCell(response: QuestionnaireResponse, path: string | undefined, caseId: string): CellValue {
    if (!path) return undefined;
    const element = path.startsWith(RESPONSE_PATH_PREFIX) ? path.slice(RESPONSE_PATH_PREFIX.length) : path;
    if (element === 'id') return caseId;
    return asCellValue((response as unknown as Record<string, unknown>)[element]);
}

function readAnswerCell(items: QuestionnaireResponseItem[] | undefined, linkId: string | undefined): CellValue {
    if (!linkId) return undefined;
    const answer = findItem(items, linkId)?.answer?.[0];
    // Missing answer leaves the key undefined and the table renders an empty cell.
    return answer ? flattenAnswer(answer) : undefined;
}

/** Depth-first search for a linkId — QuestionnaireResponse items nest. */
function findItem(
    items: QuestionnaireResponseItem[] | undefined,
    linkId: string,
): QuestionnaireResponseItem | undefined {
    for (const item of items ?? []) {
        if (item.linkId === linkId) return item;
        const nested = findItem(item.item, linkId);
        if (nested) return nested;
    }
    return undefined;
}

function flattenAnswer(answer: Answer): CellValue {
    if (answer.valueCoding) return answer.valueCoding.display ?? answer.valueCoding.code;
    return (
        answer.valueString ??
        answer.valueInteger ??
        answer.valueDecimal ??
        answer.valueBoolean ??
        answer.valueDate ??
        answer.valueDateTime ??
        answer.valueTime ??
        answer.valueUri
    );
}

/** Guard the row's index signature: anything non-primitive would render as '[object Object]'. */
function asCellValue(value: unknown): CellValue {
    return typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean' ? value : undefined;
}
